import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus,
  Image as ImageIcon,
  Eye,
  EyeOff,
  Trash2,
  GripVertical,
  Edit,
  X,
  Save,
  Upload,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Card,
  CardContent,
} from "@/components/ui/card";
import ImageSelector from "@/components/admin/ImageSelector";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminHeroSlides = () => {
  const { t } = useTranslation();
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingSlide, setEditingSlide] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    image_url: "",
    title: { fr: "", en: "", ar: "", nl: "", es: "" },
    subtitle: { fr: "", en: "", ar: "", nl: "", es: "" },
  });

  const fetchSlides = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const res = await axios.get(`${API}/admin/hero-slides`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSlides(res.data);
    } catch (error) {
      console.error("Error fetching slides:", error);
      toast.error("Erreur lors du chargement des slides");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSlides();
  }, []);

  const handleCreate = async () => {
    if (!formData.image_url) {
      toast.error("Veuillez sélectionner une image");
      return;
    }

    try {
      const token = localStorage.getItem("admin_token");
      await axios.post(`${API}/admin/hero-slides`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Slide créé avec succès");
      setIsDialogOpen(false);
      resetForm();
      fetchSlides();
    } catch (error) {
      console.error("Error creating slide:", error);
      toast.error("Erreur lors de la création");
    }
  };

  const handleUpdate = async () => {
    if (!editingSlide) return;

    try {
      const token = localStorage.getItem("admin_token");
      await axios.put(`${API}/admin/hero-slides/${editingSlide.id}`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Slide mis à jour");
      setEditingSlide(null);
      setIsDialogOpen(false);
      resetForm();
      fetchSlides();
    } catch (error) {
      console.error("Error updating slide:", error);
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleToggleVisibility = async (slide) => {
    try {
      const token = localStorage.getItem("admin_token");
      const res = await axios.post(
        `${API}/admin/hero-slides/${slide.id}/toggle-visibility`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.is_visible ? "Slide visible" : "Slide masqué");
      fetchSlides();
    } catch (error) {
      console.error("Error toggling visibility:", error);
      toast.error("Erreur lors de la modification");
    }
  };

  const handleDelete = async (slideId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce slide ?")) return;

    try {
      const token = localStorage.getItem("admin_token");
      await axios.delete(`${API}/admin/hero-slides/${slideId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Slide supprimé");
      fetchSlides();
    } catch (error) {
      console.error("Error deleting slide:", error);
      toast.error("Erreur lors de la suppression");
    }
  };

  const openEditDialog = (slide) => {
    setEditingSlide(slide);
    setFormData({
      image_url: slide.image_url || "",
      title: slide.title || { fr: "", en: "", ar: "", nl: "", es: "" },
      subtitle: slide.subtitle || { fr: "", en: "", ar: "", nl: "", es: "" },
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      image_url: "",
      title: { fr: "", en: "", ar: "", nl: "", es: "" },
      subtitle: { fr: "", en: "", ar: "", nl: "", es: "" },
    });
    setEditingSlide(null);
  };

  const handleImageSelect = (url) => {
    setFormData((prev) => ({ ...prev, image_url: url }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-hero-slides">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-secondary">Carrousel Hero</h1>
          <p className="text-muted-foreground">
            Gérez les images du carrousel de la page d'accueil
          </p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button data-testid="add-slide-btn">
              <Plus className="h-4 w-4 mr-2" />
              Ajouter un slide
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingSlide ? "Modifier le slide" : "Nouveau slide"}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-6 py-4">
              {/* Image Selection */}
              <div className="space-y-2">
                <Label>Image *</Label>
                <ImageSelector
                  value={formData.image_url}
                  onChange={handleImageSelect}
                  category="general"
                />
              </div>

              {/* Preview */}
              {formData.image_url && (
                <div className="relative aspect-video rounded-lg overflow-hidden border">
                  <img
                    src={formData.image_url}
                    alt="Preview"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/60" />
                  {formData.title?.fr && (
                    <div className="absolute inset-0 flex items-center justify-center text-center p-4">
                      <div>
                        <h3 className="text-white font-serif text-xl font-bold drop-shadow-lg">
                          {formData.title.fr}
                        </h3>
                        {formData.subtitle?.fr && (
                          <p className="text-white/80 text-sm mt-1 drop-shadow">
                            {formData.subtitle.fr}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Titles */}
              <div className="space-y-4">
                <Label className="text-base font-semibold">Titres (optionnel)</Label>
                <div className="grid grid-cols-2 gap-3">
                  {["fr", "en", "ar", "nl", "es"].map((lang) => (
                    <div key={lang} className="space-y-1">
                      <Label className="text-xs uppercase text-muted-foreground">
                        {lang === "fr" ? "🇫🇷 Français" :
                         lang === "en" ? "🇬🇧 English" :
                         lang === "ar" ? "🇸🇦 العربية" :
                         lang === "nl" ? "🇳🇱 Nederlands" : "🇪🇸 Español"}
                      </Label>
                      <Input
                        value={formData.title?.[lang] || ""}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            title: { ...prev.title, [lang]: e.target.value },
                          }))
                        }
                        placeholder={`Titre ${lang.toUpperCase()}`}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Subtitles */}
              <div className="space-y-4">
                <Label className="text-base font-semibold">Sous-titres (optionnel)</Label>
                <div className="grid grid-cols-2 gap-3">
                  {["fr", "en", "ar", "nl", "es"].map((lang) => (
                    <div key={lang} className="space-y-1">
                      <Label className="text-xs uppercase text-muted-foreground">
                        {lang === "fr" ? "🇫🇷 Français" :
                         lang === "en" ? "🇬🇧 English" :
                         lang === "ar" ? "🇸🇦 العربية" :
                         lang === "nl" ? "🇳🇱 Nederlands" : "🇪🇸 Español"}
                      </Label>
                      <Input
                        value={formData.subtitle?.[lang] || ""}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            subtitle: { ...prev.subtitle, [lang]: e.target.value },
                          }))
                        }
                        placeholder={`Sous-titre ${lang.toUpperCase()}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setIsDialogOpen(false);
                resetForm();
              }}>
                Annuler
              </Button>
              <Button onClick={editingSlide ? handleUpdate : handleCreate}>
                <Save className="h-4 w-4 mr-2" />
                {editingSlide ? "Mettre à jour" : "Créer"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Slides Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {slides.map((slide, index) => (
          <Card
            key={slide.id}
            className={`overflow-hidden transition-all ${
              !slide.is_visible ? "opacity-50" : ""
            }`}
            data-testid={`slide-card-${index}`}
          >
            <div className="relative aspect-video">
              <img
                src={slide.image_url}
                alt={slide.title?.fr || "Slide"}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              
              {/* Order Badge */}
              <div className="absolute top-2 left-2 bg-white/90 rounded-full px-2 py-1 text-xs font-medium">
                #{index + 1}
              </div>

              {/* Visibility Badge */}
              {!slide.is_visible && (
                <div className="absolute top-2 right-2 bg-red-500 text-white rounded-full px-2 py-1 text-xs">
                  Masqué
                </div>
              )}

              {/* Title Overlay */}
              {slide.title?.fr && (
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <h3 className="text-white font-semibold text-lg drop-shadow-lg line-clamp-2">
                    {slide.title.fr}
                  </h3>
                  {slide.subtitle?.fr && (
                    <p className="text-white/80 text-sm line-clamp-1">
                      {slide.subtitle.fr}
                    </p>
                  )}
                </div>
              )}
            </div>

            <CardContent className="p-4">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={slide.is_visible}
                    onCheckedChange={() => handleToggleVisibility(slide)}
                    data-testid={`slide-visibility-${index}`}
                  />
                  <span className="text-sm text-muted-foreground">
                    {slide.is_visible ? "Visible" : "Masqué"}
                  </span>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => openEditDialog(slide)}
                    data-testid={`edit-slide-${index}`}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-destructive hover:text-destructive"
                    onClick={() => handleDelete(slide.id)}
                    data-testid={`delete-slide-${index}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Empty State */}
      {slides.length === 0 && (
        <div className="text-center py-20">
          <ImageIcon className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-secondary mb-2">
            Aucun slide
          </h3>
          <p className="text-muted-foreground mb-4">
            Ajoutez des images pour le carrousel de la page d'accueil
          </p>
          <Button onClick={() => setIsDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Ajouter un slide
          </Button>
        </div>
      )}
    </div>
  );
};

export default AdminHeroSlides;
